import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Settings, Zap, Crown, Lock } from 'lucide-react';
import {
  DEFAULT_AI_MODEL,
  DEFAULT_AI_PROVIDER,
} from '@/config/aiDefaults';
import { isSystemAdminRole } from '@/lib/roles';
import { isFeatureEnabled } from '@/config/featureFlags';

interface UserPreferences {
  subscriptionTier: string;
  advancedMode: boolean;
  defaultProvider: string;
  defaultModel: string;
}

interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: Array<{
    id: string;
    name: string;
    description: string;
    available: boolean;
  }>;
  available: boolean;
}

export default function UserPreferences() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isSystemAdmin = isSystemAdminRole(user?.role);
  const [isUpdating, setIsUpdating] = useState(false);

  // AR-291: the Default AI Model section stays hidden until the feature is live
  const showDefaultAiModel = isFeatureEnabled('profileDefaultAiModel');
  const hasVisibleSections = isSystemAdmin || showDefaultAiModel;

  // Fetch user preferences - nothing on this page reads them while every section is hidden (AR-291)
  const { data: preferences, isLoading: isLoadingPreferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
    enabled: !!user && hasVisibleSections,
  });

  // Fetch available providers - only consumed by the Default AI Model card (AR-291)
  const { data: availableProviders, isLoading: isLoadingProviders } = useQuery<ChatProvider[]>({
    queryKey: ['/api/chat/providers'],
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: showDefaultAiModel,
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      return await apiRequest('PATCH', '/api/user/preferences', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      toast({
        title: 'Preferences updated',
        description: 'Your preferences have been saved successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update preferences',
        variant: 'destructive',
      });
    },
  });

  // Get available models for selected provider
  const getAvailableModels = (provider: string) => {
    if (!availableProviders) return [];
    
    const providerData = availableProviders.find((p) => p.provider === provider);
    return providerData?.models || [];
  };

  // Handle provider change
  const handleProviderChange = (newProvider: string) => {
    const models = getAvailableModels(newProvider);
    const defaultModel = models.length > 0 ? models[0].id : DEFAULT_AI_MODEL;
    
    updatePreferencesMutation.mutate({
      defaultProvider: newProvider,
      defaultModel: defaultModel,
    });
  };

  // Handle model change
  const handleModelChange = (newModel: string) => {
    updatePreferencesMutation.mutate({
      defaultModel: newModel,
    });
  };

  const getTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'PRO':
        return 'default';
      case 'FREE':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'PRO':
        return <Crown className="h-4 w-4" />;
      case 'FREE':
        return <Lock className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  if (isLoadingPreferences || isLoadingProviders) {
    return (
      <div className="container mx-auto py-6">
        <div className="max-w-2xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6" />
          <h1 className="text-2xl font-bold">User Preferences</h1>
        </div>

        {isSystemAdmin && (
          <>
            {/* Subscription Tier Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getTierIcon(preferences?.subscriptionTier || 'FREE')}
                  Subscription Tier
                </CardTitle>
                <CardDescription>
                  Your current subscription tier determines available features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Badge variant={getTierBadgeVariant(preferences?.subscriptionTier || 'FREE')}>
                      {preferences?.subscriptionTier || 'FREE'}
                    </Badge>
                    <p className="text-sm text-gray-600 mt-2">
                      {preferences?.subscriptionTier === 'PRO' 
                        ? 'You have access to advanced features including provider and model selection.'
                        : 'Upgrade to Pro to access advanced features like provider and model selection.'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Advanced Mode Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Advanced Mode
                </CardTitle>
                <CardDescription>
                  Enable advanced features like provider and model selection
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">
                      Advanced Mode
                    </Label>
                    <p className="text-sm text-gray-600">
                      {preferences?.advancedMode 
                        ? 'Advanced features are enabled'
                        : 'Advanced features are disabled'
                      }
                    </p>
                  </div>
                  <Switch
                    aria-label="Advanced Mode"
                    checked={preferences?.advancedMode || false}
                    disabled={
                      preferences?.subscriptionTier === 'FREE' && !isSystemAdmin
                    }
                    onCheckedChange={(checked) => {
                      if (preferences?.subscriptionTier === 'FREE' && !isSystemAdmin) {
                        toast({
                          title: 'Pro subscription required',
                          description: 'Advanced mode is only available for Pro subscribers.',
                          variant: 'destructive',
                        });
                        return;
                      }
                      updatePreferencesMutation.mutate({ advancedMode: checked });
                    }}
                  />
                </div>
                {preferences?.subscriptionTier === 'FREE' && !isSystemAdmin && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <Lock className="h-4 w-4 inline mr-1" />
                      Advanced mode requires a Pro subscription
                    </p>
                  </div>
                )}
                {preferences?.subscriptionTier === 'FREE' && isSystemAdmin && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">
                      <Crown className="h-4 w-4 inline mr-1" />
                      Admin override: You can enable advanced mode regardless of subscription tier
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Default Model Selection Card - gated by the profileDefaultAiModel flag (AR-291) */}
        {showDefaultAiModel && (
        <Card>
          <CardHeader>
            <CardTitle>Default AI Model</CardTitle>
            <CardDescription>
              Choose your preferred AI provider and model for new conversations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select
                value={preferences?.defaultProvider || DEFAULT_AI_PROVIDER}
                onValueChange={handleProviderChange}
                disabled={
                  !preferences?.advancedMode ||
                  (preferences?.subscriptionTier === 'FREE' && !isSystemAdmin)
                }
              >
                <SelectTrigger
                  className={
                    preferences?.advancedMode &&
                    (preferences?.subscriptionTier === 'PRO' || isSystemAdmin)
                      ? ''
                      : 'opacity-50'
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableProviders?.map((provider) => (
                    <SelectItem key={provider.provider} value={provider.provider}>
                      <div className="flex items-center gap-2">
                        <span>{provider.name || provider.provider}</span>
                        <Badge variant="outline" className="text-xs">
                          {provider.provider}
                        </Badge>
                        {!provider.available && (
                          <Badge variant="destructive" className="text-xs">
                            Unavailable
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Model</Label>
              <Select
                value={preferences?.defaultModel || DEFAULT_AI_MODEL}
                onValueChange={handleModelChange}
                disabled={
                  !preferences?.advancedMode ||
                  (preferences?.subscriptionTier === 'FREE' && !isSystemAdmin)
                }
              >
                <SelectTrigger
                  className={
                    preferences?.advancedMode &&
                    (preferences?.subscriptionTier === 'PRO' || isSystemAdmin)
                      ? ''
                      : 'opacity-50'
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableModels(
                    preferences?.defaultProvider || DEFAULT_AI_PROVIDER,
                  ).map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      <div className="flex flex-col">
                        <span>{model.name}</span>
                        <span className="text-sm text-gray-500">{model.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Nothing to configure yet - avoids leaving a bare page when every section is flagged off (AR-291) */}
        {!hasVisibleSections && (
          <p className="text-sm text-muted-foreground">
            There are no preferences to configure yet. New options will appear here as features become available.
          </p>
        )}

        {/* Save Button */}
        {hasVisibleSections && (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              toast({
                title: 'Preferences saved',
                description: 'Your preferences are automatically saved when you make changes.',
              });
            }}
            disabled={updatePreferencesMutation.isPending}
          >
            {updatePreferencesMutation.isPending ? 'Saving...' : 'Preferences Auto-Save'}
          </Button>
        </div>
        )}
      </div>
    </div>
  );
} 
